// src/products/dataroom/components/Modals/UploadFileModal.js
import React, { useState, useCallback } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../../../config';
import { validateFileSecurity } from '../../utils/fileSecurityRules';

const UploadFileModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  currentFolder, 
  onUploadComplete 
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState('');

  // File validation using security rules
  const validateFile = (file) => {
    const validation = validateFileSecurity(file);
    
    if (!validation.isValid) {
      // Return first error message
      return validation.errors[0] || 'File validation failed';
    }
    
    // Log warnings (non-blocking)
    if (validation.warnings.length > 0) {
      console.warn(`File validation warning for ${file.name}:`, validation.warnings);
    }
    
    return null;
  };

  // Handle file processing (used by both file select and drag-drop)
  const processFiles = useCallback((fileList) => {
    const selectedFiles = Array.from(fileList);
    const validFiles = [];
    const newErrors = [];

    selectedFiles.forEach((file) => {
      const error = validateFile(file);
      if (error) {
        newErrors.push(`${file.name}: ${error}`);
        // Show red popup for blocked files
        if (error.includes('FILE NOT ALLOWED')) {
          setErrorPopupMessage(error);
          setShowErrorPopup(true);
          // Auto-hide after 8 seconds
          setTimeout(() => setShowErrorPopup(false), 8000);
        }
      } else {
        validFiles.push({
          file,
          id: Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          status: 'pending'
        });
      }
    });

    setFiles(prev => [...prev, ...validFiles]);
    setErrors(newErrors);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  }, [processFiles]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop event
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Upload single file to Supabase
  const uploadFile = async (fileItem) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create unique file path - ensure proper folder structure
      const timestamp = Date.now();
      const sanitizedName = fileItem.file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const folderPath = currentFolder ? `${currentFolder}` : 'root';
      const filePath = `${projectId}/${folderPath}/${timestamp}_${sanitizedName}`;

      console.log('Uploading file to path:', filePath);

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('dataroom-files')
        .upload(filePath, fileItem.file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      console.log('File uploaded successfully:', uploadData);

      // Get file info for database
      const fileType = fileItem.file.type;
      const fileCategory = getFileCategory(fileType);

      // Save file metadata to database
      const { data: dbData, error: dbError } = await supabase
        .from('dataroom_items')
        .insert({
          name: fileItem.file.name,
          type: 'file',
          is_folder: false,
          project_id: parseInt(projectId),
          parent_id: currentFolder ? parseInt(currentFolder) : null,
          file_path: filePath,
          file_size: fileItem.file.size,
          file_type: fileType,
          file_category: fileCategory,
          created_by: user.id
        })
        .select();

      if (dbError) {
        console.error('Database insert error:', dbError);
        throw dbError;
      }

      console.log('File record created:', dbData);

      // Log upload activity
      await supabase
        .from('dataroom_logs')
        .insert({
          user_id: user.id,
          project_id: parseInt(projectId),
          action: 'file_uploaded',
          details: {
            file_name: fileItem.file.name,
            file_size: fileItem.file.size,
            file_path: filePath
          }
        });

      return { success: true, data: dbData[0] };
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, error: error.message };
    }
  };

  // Get file category based on type
  const getFileCategory = (fileType) => {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType === 'application/pdf') return 'pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'document';
    if (fileType.includes('sheet') || fileType.includes('excel')) return 'spreadsheet';
    if (fileType.startsWith('text/')) return 'text';
    return 'other';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle upload process
  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    let successful = 0;
    let failed = 0;

    for (const fileItem of files) {
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ));

      const result = await uploadFile(fileItem);

      if (result.success) {
        successful++;
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'completed' } : f
        ));
      } else {
        failed++;
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'error', error: result.error } : f
        ));
      }
    }

    setUploading(false);
    
    // Auto-close after successful upload
    setTimeout(() => {
      if (onUploadComplete) {
        onUploadComplete({ successful, failed });
      }
    }, 1500);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-600" />;
      default: return <FileText className="h-4 w-4 text-gray-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Red Error Popup */}
      {showErrorPopup && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className="bg-red-600 text-white rounded-lg shadow-2xl p-6 max-w-md border-2 border-red-700">
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-bold text-lg mb-2">File Not Allowed</h4>
                <p className="text-sm leading-relaxed">{errorPopupMessage}</p>
              </div>
              <button
                onClick={() => setShowErrorPopup(false)}
                className="ml-4 text-white hover:text-red-200 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Upload className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">Upload Files</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* File Upload Area */}
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              Drop files here or click to browse
            </h4>
            <p className="text-gray-500 mb-4">
              Support for PDF, Word, Excel, Images, Text files (Max 100MB each)
            </p>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50"
            >
              Select Files
            </label>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h5 className="text-sm font-medium text-red-800 mb-2">Upload Errors:</h5>
              <ul className="text-sm text-red-700 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Files List */}
          {files.length > 0 && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-gray-900 mb-3">
                Files to Upload ({files.length})
              </h5>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {files.map((fileItem) => (
                  <div
                    key={fileItem.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center flex-1">
                      {getStatusIcon(fileItem.status)}
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {fileItem.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(fileItem.size)}
                          {fileItem.error && (
                            <span className="text-red-600 ml-2">
                              - {fileItem.error}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {!uploading && fileItem.status === 'pending' && (
                      <button
                        onClick={() => removeFile(fileItem.id)}
                        className="text-gray-400 hover:text-red-600 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload {files.length} Files
              </>
            )}
          </button>
        </div>
      </div>
      </div>
      
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default UploadFileModal;